import { useState, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Sparkles, Loader2, ChevronRight, ChevronLeft,
  Check, X, Plus, Trash2, AlertCircle, Utensils
} from "lucide-react";

export function AIRecipeWizard({ open, onClose, stockItems, onSaved }) {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios); authAxiosRef.current = authAxios;
  const [step, setStep] = useState("idle"); // idle | loading | review | done
  const [suggestions, setSuggestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [force, setForce] = useState(false);
  const [systemSkipped, setSystemSkipped] = useState(0);

  const stockMap = Object.fromEntries(stockItems.map(s => [s.id, s]));

  const runAI = useCallback(async () => {
    setStep("loading");
    try {
      const res = await authAxiosRef.current.post(
        "/stock/recipes/ai-suggest",
        { force },
        { timeout: 120000, _noRetry: true }
      );
      const sugs = res.data.suggestions || [];
      const sys = res.data.skipped || 0;
      setSystemSkipped(sys);
      if (sugs.length === 0) {
        const msg = sys > 0
          ? `${sys} produto(s) já têm ficha sem alterações. Ative "Forçar regeneração" para regerar.`
          : (res.data.message || "Nenhum produto para gerar fichas");
        toast.info(msg);
        setStep("idle");
        return;
      }
      setSuggestions(sugs.map(s => ({
        ...s,
        ingredients: (s.ingredients || []).map(i => ({ ...i })),
        _skip: false,
      })));
      setCurrent(0);
      setStep("review");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = detail
        || (err.code === "ECONNABORTED" ? "Timeout: a IA demorou demasiado. Tente novamente." : null)
        || err.message
        || "Erro ao comunicar com a IA";
      toast.error(msg);
      setStep("idle");
    }
  }, [force]);

  const updateQty = (ingIdx, val) => {
    setSuggestions(prev => {
      const copy = prev.map((s, si) =>
        si === current
          ? { ...s, ingredients: s.ingredients.map((ing, ii) => ii === ingIdx ? { ...ing, quantity: parseFloat(val) || 0 } : ing) }
          : s
      );
      return copy;
    });
  };

  const removeIng = (ingIdx) => {
    setSuggestions(prev => prev.map((s, si) =>
      si === current ? { ...s, ingredients: s.ingredients.filter((_, ii) => ii !== ingIdx) } : s
    ));
  };

  const addIng = () => {
    const first = stockItems[0];
    if (!first) return;
    setSuggestions(prev => prev.map((s, si) =>
      si === current ? {
        ...s,
        ingredients: [...s.ingredients, { stock_id: first.id, ingredient_name: first.name, unit: first.unit, quantity: 1 }]
      } : s
    ));
  };

  const changeIngredient = (ingIdx, stockId) => {
    const s = stockMap[stockId];
    setSuggestions(prev => prev.map((sug, si) =>
      si === current ? {
        ...sug,
        ingredients: sug.ingredients.map((ing, ii) =>
          ii === ingIdx ? { ...ing, stock_id: stockId, ingredient_name: s?.name || "?", unit: s?.unit || "" } : ing
        )
      } : sug
    ));
  };

  const saveCurrentAndNext = async () => {
    const sug = suggestions[current];
    if (!sug._skip && sug.ingredients.length > 0) {
      setSaving(true);
      try {
        await authAxios.post("/stock/recipes", {
          product_id: sug.product_id,
          ingredients: sug.ingredients.map(i => ({ stock_id: i.stock_id, quantity: i.quantity })),
        });
        setSaved(n => n + 1);
        toast.success(`Ficha de "${sug.product_name}" guardada!`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erro ao guardar");
      }
      setSaving(false);
    } else {
      setSkipped(n => n + 1);
    }

    if (current + 1 >= suggestions.length) {
      setStep("done");
      onSaved?.();
    } else {
      setCurrent(n => n + 1);
    }
  };

  const skip = () => {
    setSkipped(n => n + 1);
    if (current + 1 >= suggestions.length) { setStep("done"); onSaved?.(); }
    else setCurrent(n => n + 1);
  };

  const reset = () => { setStep("idle"); setSuggestions([]); setCurrent(0); setSaved(0); setSkipped(0); setSystemSkipped(0); };

  const handleClose = () => { reset(); onClose(); };

  const sug = suggestions[current];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm font-bold uppercase">
            <Sparkles className="w-4 h-4 text-violet-500" />
            IA — Fichas Técnicas Automáticas
          </DialogTitle>
        </DialogHeader>

        {/* IDLE */}
        {step === "idle" && (
          <div className="py-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto">
              <Sparkles className="w-8 h-8 text-violet-600" />
            </div>
            <div>
              <p className="font-semibold text-lg">Gerar Fichas com IA</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                A IA analisa cada produto do seu menu e sugere os ingredientes e quantidades com base no stock disponível. Você revisa e aprova cada ficha.
              </p>
            </div>
            {stockItems.length === 0 ? (
              <div className="flex items-center gap-2 justify-center text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                Adicione ingredientes ao inventário antes de usar esta função
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Button onClick={runAI} className="gap-2 bg-violet-600 hover:bg-violet-700">
                  <Sparkles className="w-4 h-4" />Analisar Produtos com IA
                </Button>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                  <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} className="w-3.5 h-3.5 accent-violet-600" />
                  Forçar regeneração de fichas já existentes
                </label>
              </div>
            )}
          </div>
        )}

        {/* LOADING */}
        {step === "loading" && (
          <div className="py-12 text-center space-y-3">
            <Loader2 className="w-10 h-10 animate-spin text-violet-500 mx-auto" />
            <p className="font-medium">A IA está analisando os produtos...</p>
            <p className="text-sm text-muted-foreground">Pode demorar alguns segundos</p>
          </div>
        )}

        {/* REVIEW */}
        {step === "review" && sug && (
          <div className="space-y-4">
            {/* Progress */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Produto {current + 1} de {suggestions.length}</span>
              <span>
                {saved} guardados · {skipped} pulados
                {systemSkipped > 0 && ` · ${systemSkipped} ignorados (sem alterações)`}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div className="bg-violet-500 h-1.5 rounded-full transition-all" style={{ width: `${((current) / suggestions.length) * 100}%` }} />
            </div>

            {/* Product header */}
            <Card className="border-violet-200 bg-violet-50/50">
              <CardContent className="p-4 flex items-center gap-3">
                <Utensils className="w-8 h-8 text-violet-600 shrink-0" />
                <div>
                  <p className="font-bold text-lg">{sug.product_name}</p>
                  {sug.product_price > 0 && <p className="text-sm text-muted-foreground">Preço: {sug.product_price?.toFixed(2)}EUR</p>}
                  {sug.notes && <p className="text-xs text-violet-700 mt-0.5 italic">{sug.notes}</p>}
                </div>
              </CardContent>
            </Card>

            {/* Ingredients */}
            <div>
              <p className="text-xs font-bold uppercase text-muted-foreground mb-2">Ingredientes sugeridos</p>
              {sug.ingredients.length === 0 ? (
                <div className="text-center py-4 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
                  Nenhum ingrediente mapeado. Adicione manualmente ou pule este produto.
                </div>
              ) : (
                <div className="space-y-2">
                  {sug.ingredients.map((ing, ii) => (
                    <div key={ii} className="flex items-center gap-2">
                      <select
                        value={ing.stock_id}
                        onChange={e => changeIngredient(ii, e.target.value)}
                        className="flex-1 text-sm border border-border rounded-md px-2 py-1.5 bg-background"
                      >
                        {stockItems.map(s => <option key={s.id} value={s.id}>{s.name} ({s.unit})</option>)}
                      </select>
                      <div className="flex items-center gap-1 w-28">
                        <Input
                          type="number"
                          min="0"
                          step="any"
                          value={ing.quantity}
                          onChange={e => updateQty(ii, e.target.value)}
                          className="h-8 text-sm w-20"
                        />
                        <span className="text-xs text-muted-foreground w-6 shrink-0">{ing.unit}</span>
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => removeIng(ii)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <Button variant="outline" size="sm" onClick={addIng} className="mt-2 gap-1 text-xs">
                <Plus className="w-3 h-3" />Adicionar ingrediente
              </Button>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={skip} disabled={saving} className="gap-1 flex-1">
                <X className="w-4 h-4" />Pular
              </Button>
              <Button
                onClick={saveCurrentAndNext}
                disabled={saving || sug.ingredients.length === 0}
                className="gap-1 flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {current + 1 === suggestions.length ? "Guardar e Finalizar" : "Guardar e Continuar"}
              </Button>
            </div>
          </div>
        )}

        {/* DONE */}
        {step === "done" && (
          <div className="py-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <p className="font-bold text-xl">Concluído!</p>
              <p className="text-muted-foreground text-sm mt-1">
                {saved} {saved === 1 ? "ficha guardada" : "fichas guardadas"} · {skipped} puladas
              </p>
            </div>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={reset}>Analisar novamente</Button>
              <Button onClick={handleClose}>Fechar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
